import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { adminMenuItems } from '../../../../config/layout';
import LegalEntities from './LegalEntities';
import Location from './Location';
import Department from './Department';
import Designation from './designation/Designation';
import PermissionGuard from '../../../../components/PermissionGuard';

/**
 * OrgStructure Main Component
 * Serves as the main page for organization structure management
 * Handles tabs navigation and breadcrumb
 */
const OrgStructure = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([]);

  // Get tabs from layout configuration
  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentnavItem = useMemo(() => {
    return orgManagementItem?.subItems?.find((item) => item.id === 'org-structure');
  }, [orgManagementItem]);

  useEffect(() => {
    if (currentnavItem?.tabs) {
      setTabs(currentnavItem.tabs);
    }
  }, [currentnavItem]);

  useEffect(() => {
    if (!tab) {
      setActiveTab(0);
      return;
    }

    const nextIndex = tabs.findIndex((t) => t.value === tab);
    if (nextIndex !== -1) {
      setActiveTab(nextIndex);
    }
  }, [tab, tabs]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(`/org-management/org-structure/${tabs[newValue].value}`);
  };

  // Get breadcrumbs - use active tab's breadcrumbs if available, otherwise use base breadcrumbs
  const currentBreadcrumbs = useMemo(() => {
    if (activeTab >= 0 && tabs[activeTab]?.breadcrumbs) {
      return tabs[activeTab].breadcrumbs;
    }
    return currentnavItem?.breadcrumbs || [];
  }, [activeTab, tabs, currentnavItem]);

  return (
    <Container maxWidth="xl" sx={{ m: 0, p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            underline={index === currentBreadcrumbs.length - 1 ? 'none' : 'hover'}
            color={index === currentBreadcrumbs.length - 1 ? 'inherit' : 'inherit'}
            sx={{
              cursor: index === currentBreadcrumbs.length - 1 ? 'default' : 'pointer',
              color: index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#4F4937',
              fontWeight: index === currentBreadcrumbs.length - 1 ? 600 : 400,
              '&:hover': {
                color:
                  index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#7522D1',
              },
            }}
            onClick={() => index < currentBreadcrumbs.length - 1 && navigate(crumb.path)}
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#030007',
            marginBottom: 1,
          }}
        >
          Org Structure
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#4F4937',
            fontSize: '0.95rem',
            lineHeight: 1.6,
          }}
        >
          Manage your team members, assign roles, and collaborate efficiently on
          tests and certifications.
        </Typography>
      </Box>

      {/* Tabs Section */}
      <Box
        sx={{
          marginBottom: 3,
          display: 'flex',
          maxWidth: '100%',
          width: 'fit-content',
          backgroundColor: '#F5EEFC',
          padding: '6px',
          borderRadius: '16px',
          gap: 0.5,
          border: '1px solid rgba(117, 34, 209, 0.08)',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {tabs.map((tab, index) => {
          const moduleMap = {
            'legal-entities': 'entities',
            'location': 'entities',
            'department': 'departments',
            'designation': 'designations'
          };
          return (
            <PermissionGuard key={tab.value} module={moduleMap[tab.value] || tab.value} action="view">
              <Button
                onClick={() => handleTabChange(null, index)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontWeight: activeTab === index ? 600 : 500,
                  paddingX: 3,
                  paddingY: 1,
                  borderRadius: '12px',
                  color: activeTab === index ? '#FFFFFF' : '#42374F',
                  backgroundColor: activeTab === index ? '#7522D1' : 'transparent',
                  boxShadow: activeTab === index ? '0 4px 12px rgba(117, 34, 209, 0.25)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: activeTab === index ? '#7522D1' : 'rgba(117, 34, 209, 0.06)',
                    color: activeTab === index ? '#FFFFFF' : '#7522D1',
                    transform: activeTab === index ? 'none' : 'translateY(-1px)',
                  },
                }}
              >
                {tab.label}
              </Button>
            </PermissionGuard>
          );
        })}
      </Box>

      {/* Tab Content */}
      <Box sx={{ paddingTop: 1 }}>
        {activeTab === 0 && (
          <PermissionGuard module="entities" action="view">
            <LegalEntities />
          </PermissionGuard>
        )}
        {activeTab === 1 && (
          <PermissionGuard module="entities" action="view">
            <Location />
          </PermissionGuard>
        )}
        {activeTab === 2 && (
          <PermissionGuard module="departments" action="view">
            <Department />
          </PermissionGuard>
        )}
        {activeTab === 3 && (
          <PermissionGuard module="designations" action="view">
            <Designation />
          </PermissionGuard>
        )}
      </Box>
    </Container>
  );
};

export default OrgStructure;
